import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import Divider from '@material-ui/core/Divider'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import DuplicateIcon from 'assets/images/ui/icons/duplicate.svg'
import ExportIcon from 'assets/images/ui/icons/export.svg'
import PageHeaderButton from 'lib/ui/PageHeader/PageHeaderButton'
import Menu from 'lib/ui/Menu'
import MenuItem from 'lib/ui/Menu/MenuItem'
import ConfirmDialog from 'lib/ui/ConfirmDialog'
import {useSnackbar} from 'lib/ui/SnackbarProvider'
import {api, getEventUrl} from 'lib/url'
import {useEvent} from 'Event/EventProvider'
import {useOrganization} from 'organization/OrganizationProvider'
import {eventRoutes} from 'Event/Routes'

export default function PublicUrlButton() {
  const {client} = useOrganization()
  const {event} = useEvent()
  const {enqueueSnackbar} = useSnackbar()
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const url = api(`/events/${event.slug}/emoji_page/token`)

  useEffect(() => {
    let isMounted = true

    client
      .get<{token: string | null}>(url)
      .then((res) => {
        if (!isMounted) {
          return
        }

        setToken(res.token)
        setIsLoading(false)
      })
      .catch(() => {
        if (!isMounted) {
          return
        }

        enqueueSnackbar('Could not load the public URL. Reload the page.', {
          variant: 'error',
        })
      })

    return () => {
      isMounted = false
    }
  }, [client, url, enqueueSnackbar])

  const publicUrl = token
    ? `${getEventUrl(event)}${eventRoutes.emoji_page}?token=${token}`
    : null

  const createToken = (successMessage?: string) => {
    client
      .post<{token: string}>(url)
      .then((res) => {
        setToken(res.token)

        if (successMessage) {
          enqueueSnackbar(successMessage, {variant: 'success'})
        }
      })
      .catch(() => {
        enqueueSnackbar('Could not create a public URL. Try again.', {
          variant: 'error',
        })
      })
  }

  const copy = () => {
    if (!publicUrl) {
      return
    }

    navigator.clipboard
      .writeText(publicUrl)
      .then(() => {
        enqueueSnackbar('Public URL copied to clipboard', {variant: 'success'})
      })
      .catch(() => {
        enqueueSnackbar('Could not copy the public URL. Try again.', {
          variant: 'error',
        })
      })
  }

  const open = () => {
    if (!publicUrl) {
      return
    }

    window.open(publicUrl, '_blank', 'noopener,noreferrer')
  }

  if (isLoading) {
    return (
      <PageHeaderButton
        text="Public URL"
        variant="outlined"
        color="primary"
        endIcon={<ArrowIcon />}
        disabled
        aria-label="emoji page public url"
      />
    )
  }

  if (!token) {
    return (
      <PageHeaderButton
        text="Create Public URL"
        variant="outlined"
        color="primary"
        onClick={() => createToken()}
        aria-label="create emoji page public url"
      />
    )
  }

  return (
    <ConfirmDialog
      title="Reset public URL?"
      description="Anyone using the current public URL will lose access. You'll need to share the new URL again."
      onConfirm={() => createToken('Public URL reset. Copy it to share again.')}
      confirmLabel="Reset URL"
      variant="dangerous"
    >
      {(confirm) => (
        <Menu
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
          button={({open: openMenu}) => (
            <PageHeaderButton
              text="Public URL"
              variant="outlined"
              color="primary"
              endIcon={<ArrowIcon />}
              onClick={openMenu}
              aria-label="emoji page public url"
            />
          )}
        >
          <MenuItem
            icon={DuplicateIcon}
            children="Copy URL"
            onClick={copy}
            aria-label="copy emoji page public url"
          />
          <MenuItem
            icon={ExportIcon}
            children="Open in New Tab"
            onClick={open}
            aria-label="open emoji page public url"
          />
          <Divider />
          <MenuItem
            children="Reset URL"
            color="danger"
            onClick={() => confirm()}
            aria-label="reset emoji page public url"
          />
        </Menu>
      )}
    </ConfirmDialog>
  )
}

const ArrowIcon = styled(ArrowDropDownIcon)`
  font-size: 20px;
  margin: -4px -6px -4px -4px;
`
